import { hideNavigableControlArrowsUntilHover } from '@src/components/MarketDataCard/components/navigableControlRowStyles';
import { COLUMN_WIDTH } from '@src/styles/dimensions';
import { Box, CARD_HEIGHT, CardViewEnum, COMPACT_CARD_HEIGHT, modeColor } from '@talos/kyoko';
import styled, { css } from 'styled-components';

import { predictionLegSurfaceColor, predictionLegTextColor, type PredictionLeg } from '../predictionLegColors';

/** Portalized SearchSelect dropdown width — card stays COLUMN_WIDTH. */
export const PREDICTION_CARD_SELECTOR_DROPDOWN_WIDTH = 'min(100vw, 380px)';

export const Wrapper = styled(Box)<{
  isDragged?: boolean;
  $isSetup?: boolean;
  $showSettings?: boolean;
  $view?: string;
}>`
  position: relative;
  height: ${({ $showSettings, $view }) => {
    if ($showSettings) {
      return `${CARD_HEIGHT}px`;
    }
    return `${$view === CardViewEnum.COMPACT ? COMPACT_CARD_HEIGHT : CARD_HEIGHT}px`;
  }};
  display: flex;
  flex-direction: column;
  min-width: ${COLUMN_WIDTH}px;
  overflow: ${({ $isSetup, $showSettings }) => ($isSetup || $showSettings ? 'visible' : 'hidden')};
  color: ${({ theme }) => theme.colors.gray['100']};
  ${({ isDragged, theme }) =>
    isDragged ? `box-shadow: 0 5px 24px ${modeColor(theme, 'hsla(0, 0%, 0%, 0.1)', theme.colors.black.dim)};` : ''}

  ${hideNavigableControlArrowsUntilHover}
`;
Wrapper.defaultProps = {
  background: 'backgroundContent',
};

export const HeaderEventTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing4}px;
  width: 100%;
  min-width: 0;
`;

export const HeaderEventIcon = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.gray['090']};
`;

export const HeaderEventTitle = styled.span`
  display: block;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: ${({ theme }) => theme.fontSizeDefault}rem;
  font-weight: ${({ theme }) => theme.fontWeightMedium};
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.gray['100']};
`;

export const HeaderCategory = styled.div`
  color: ${({ theme }) => theme.colors.gray['080']};
  font-size: ${({ theme }) => theme.fontSizeSmall}rem;
  line-height: 1.25;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const CardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  width: 100%;
`;

export const Body = styled.div<{
  $isSetup?: boolean;
  /**
   * Content that owns the full body height and its own scrolling: setup, single-outcome trade layout,
   * paginated outcomes, loading, and the unavailable-event notice, which centers on both axes.
   */
  $fillsBody?: boolean;
}>`
  flex: 1;
  width: 100%;
  min-width: 0;
  min-height: 0;
  position: ${({ $isSetup }) => ($isSetup ? 'relative' : 'static')};
  display: ${({ $fillsBody }) => ($fillsBody ? 'flex' : 'block')};
  flex-direction: column;
  overflow: ${({ $fillsBody }) => ($fillsBody ? 'hidden' : 'auto')};
  padding-bottom: ${({ theme, $fillsBody }) => ($fillsBody ? 0 : theme.spacing8)}px;
`;

export const OutcomesBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

export const CardFooter = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: ${({ theme }) => `${theme.spacing4}px ${theme.spacing16}px`};
  min-height: ${({ theme }) => theme.spacing24}px;
`;

export const CardFooterSide = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;

  &:last-child {
    justify-content: flex-end;
  }
`;

export const CardFooterPagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing2}px;
  flex-shrink: 0;
`;

export const FooterNavButton = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${({ theme }) => theme.spacing16}px;
  height: ${({ theme }) => theme.spacing16}px;
  padding: 0;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadiusDefault}px;
  background: ${({ theme }) => theme.colors.gray['020']};
  color: ${({ theme }) => theme.colors.gray['090']};
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.4;
  }

  svg {
    width: ${({ theme }) => theme.spacing12}px;
    height: ${({ theme }) => theme.spacing12}px;
  }
`;

export const SelectorBody = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: ${({ theme }) => `${theme.spacing16}px ${theme.spacing16}px ${theme.spacing8}px`};
`;

export const OutcomeRow = styled.div<{ $compact?: boolean; $singleOutcome?: boolean }>`
  padding: ${({ theme, $compact, $singleOutcome }) => {
    if ($singleOutcome) {
      return '0';
    }
    return `${theme.spacing4}px ${theme.spacing16}px`;
  }};
  ${({ $singleOutcome }) =>
    $singleOutcome
      ? `
    display: flex;
    flex-direction: column;
  `
      : ''}
`;

export const SingleOutcomeTradePanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing8}px;
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing8}px ${theme.spacing16}px 0`};
`;

export const SingleOutcomeButtonRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: ${({ theme }) => theme.spacing6}px;
  width: 100%;
`;

export const OutcomeRowHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing8}px;
  margin-bottom: ${({ theme }) => theme.spacing2}px;
`;

export const HeaderOdds = styled.span`
  flex-shrink: 0;
`;

export const OutcomeOdds = styled.span`
  flex-shrink: 0;
  text-align: right;
`;

export const OutcomeButtonRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: ${({ theme }) => theme.spacing6}px;
`;

interface OutcomeActionButtonProps {
  $compact?: boolean;
}

const outcomeActionButtonCss = css<OutcomeActionButtonProps>`
  width: 100%;
  border-radius: ${({ theme }) => theme.borderRadiusDefault}px;
  padding: ${({ theme }) => `${theme.spacing4}px ${theme.spacing8}px`};
  font-size: ${({ theme, $compact }) => ($compact ? theme.fontSizeTiny : theme.fontSizeSmall)}rem;
  font-weight: ${({ theme }) => theme.fontWeightMedium};
  line-height: ${({ theme }) => theme.fontSizeDefault}rem;
  text-align: center;
  cursor: pointer;
  transition:
    background 150ms ease,
    border-color 150ms ease;

  &:disabled {
    cursor: default;
    opacity: 0.45;
  }
`;

export const OutcomePriceButton = styled.button.attrs({ type: 'button' })<
  OutcomeActionButtonProps & {
    $variant: PredictionLeg;
  }
>`
  ${outcomeActionButtonCss};
  border: 1px solid ${({ $variant, theme }) => predictionLegSurfaceColor($variant, theme, 0.12)};
  color: ${({ $variant, theme }) => predictionLegTextColor($variant, theme)};
  background: ${({ $variant, theme }) => predictionLegSurfaceColor($variant, theme, 0.12)};

  &:not(:disabled):hover {
    border-color: transparent;
    background: ${({ $variant, theme }) => predictionLegSurfaceColor($variant, theme, 0.32)};
  }
`;

export const OutcomeRFQButton = styled.button.attrs({ type: 'button' })<OutcomeActionButtonProps>`
  ${outcomeActionButtonCss};
  width: auto;
  border: 1px solid ${({ theme }) => theme.colors.gray['020']};
  color: ${({ theme }) => theme.colors.gray['090']};
  background: ${({ theme }) => theme.colors.gray['020']};

  &:not(:disabled):hover {
    border-color: ${({ theme }) => theme.colors.gray['030']};
    background: ${({ theme }) => theme.colors.gray['030']};
  }
`;
